import { useState, useCallback, useMemo } from 'react';
import { notification } from 'antd';
import { useLanguage, useGetBibleUrlParams } from 'core/hooks';
import { logError } from 'config/logging';
import { openNotificationWithIcon } from 'config/notification';
import { IBibleBook, IBookmark } from 'types';
import { NotificationType } from 'store/slices/auth/types';
import { useMemoSelector, useAppDispatch } from 'store/hooks';
import { getAuthState } from 'store/selectors/auth';
import { getBookmarks } from 'store/selectors/bookmarks';
import { createBookmark, removeBookmark } from 'store/slices/bookmarks/actions';
import { getNotes } from 'store/selectors/notes';
import { IVersesMemo } from 'components/common/Modals/memosModal/interfaces';

import { NOTES_VALUE } from './Header/TranslationsSelector';
import { useReadingPageData } from './useReadingPageData';
import { getTableColumns } from './tableColumns';
import { getTableData, isSameVerseMemo } from './utils';

export function useVersesTable() {
  const dispatch = useAppDispatch();
  const { currentLanguage } = useLanguage();
  const { userInfo } = useMemoSelector(getAuthState);
  const bookmarks = useMemoSelector(getBookmarks);
  const notes = useMemoSelector(getNotes);
  const { book, chapter, translations } = useGetBibleUrlParams();
  const { books, readingData } = useReadingPageData();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const [selectedVerses, setSelectedVerses] = useState<IVersesMemo[]>([]);

  const translationParts = translations ? translations.split(',') : [];
  const translationIds = translationParts
    .filter(p => p !== NOTES_VALUE)
    .map(Number)
    .filter(n => !Number.isNaN(n));
  const notesShown = translationParts.includes(NOTES_VALUE) && Boolean(userInfo?._id);

  const isVerseBookmarked = useCallback((verseNumber: number, translationId: number): boolean => {
    if (!userInfo?._id || !book || !chapter) return false;
    return bookmarks.some(
      (bookmark: IBookmark) => bookmark.bookNumber === book
        && bookmark.chapterNumber === chapter
        && bookmark.verseIndex === verseNumber
        && bookmark.translationId === translationId,
    );
  }, [userInfo?._id, book, chapter, bookmarks]);

  const findBookmark = useCallback((verseNumber: number, translationId: number): IBookmark | undefined => {
    if (!userInfo?._id || !book || !chapter) return undefined;
    return bookmarks.find(
      (bookmark: IBookmark) => bookmark.bookNumber === book
        && bookmark.chapterNumber === chapter
        && bookmark.verseIndex === verseNumber
        && bookmark.translationId === translationId,
    );
  }, [userInfo?._id, book, chapter, bookmarks]);

  const handleCellSelection = useCallback((verseNumber: number, columnIndex: number, cellText: string) => {
    if (!userInfo?._id || !readingData || !book || !chapter) return;
    const chapterData = readingData.chapters[columnIndex];
    const bookForColumn = books.find((b: IBibleBook) => b.id === chapterData.book_id);
    const verseData: IVersesMemo = {
      text: cellText,
      shortTitle: bookForColumn?.short_title ?? '',
      translationId: translationIds[columnIndex] ?? 0,
      verseNumber,
      chapterNumber: chapter,
      location: [book, chapter, verseNumber],
    };

    setSelectedVerses(prev => {
      const alreadySelected = prev.some(v => isSameVerseMemo(v, verseData));
      if (alreadySelected) {
        return prev.filter(v => !isSameVerseMemo(v, verseData));
      }

      return [...prev, verseData];
    });
  }, [userInfo?._id, readingData, book, chapter, books, translationIds]);

  const handleBookmarkClick = useCallback(async (verseNumber: number, columnIndex: number) => {
    if (!userInfo?._id || !readingData || !book || !chapter) return;

    const translationId = translationIds[columnIndex] ?? 0;
    const existingBookmark = findBookmark(verseNumber, translationId);

    try {
      if (existingBookmark) {
        await dispatch(removeBookmark({ id: existingBookmark._id })).unwrap();
        openNotificationWithIcon({
          api: notificationApi,
          type: NotificationType.success,
          description: currentLanguage.un_marked,
          placement: 'top',
        });
      } else {
        if (bookmarks.length >= 20) {
          openNotificationWithIcon({
            api: notificationApi,
            type: NotificationType.warning,
            description: currentLanguage.bookmark_limit,
            placement: 'top',
          });
          return;
        }

        const bookForColumn = books.find((b: IBibleBook) => b.id === readingData.chapters[columnIndex].book_id);
        const title = `${bookForColumn?.short_title ?? ''} ${chapter}:${verseNumber}`;

        await dispatch(
          createBookmark({
            bookNumber: book,
            chapterNumber: chapter,
            verseIndex: verseNumber,
            translationId,
            title,
          }),
        ).unwrap();

        openNotificationWithIcon({
          api: notificationApi,
          type: NotificationType.success,
          description: currentLanguage.bookmark_created,
          placement: 'top',
        });
      }
    } catch (error) {
      logError(error, 'Error handling bookmark');
    }
  }, [
    userInfo?._id, readingData, book, chapter, books, translationIds,
    findBookmark, dispatch, notificationApi, currentLanguage, bookmarks.length,
  ]);

  const columns = useMemo(() => {
    if (!readingData) return [];
    return getTableColumns({
      readingData,
      selectedVerses,
      translationIds,
      onCellSelection: handleCellSelection,
      onBookmarkClick: handleBookmarkClick,
      isVerseBookmarked,
      notesConfig: notesShown
        ? {
          notes,
          bookNumber: book!,
          chapterNumber: chapter!,
          notesColumnTitle: currentLanguage.notes ?? 'Notes',
        }
        : undefined,
      notesIndicatorConfig: userInfo?._id && !notesShown && book != null && chapter != null
        ? { notes, bookNumber: book, chapterNumber: chapter }
        : undefined,
      isUser: Boolean(userInfo?._id),
    });
  }, [
    readingData,
    selectedVerses,
    translationIds,
    handleCellSelection,
    handleBookmarkClick,
    isVerseBookmarked,
    notesShown,
    notes,
    book,
    chapter,
    userInfo?._id,
    currentLanguage.notes,
  ]);
  const tableData = useMemo(
    () => (readingData ? getTableData(readingData) : []),
    [readingData],
  );

  return {
    columns,
    tableData,
    selectedVerses,
    setSelectedVerses,
    handleCellSelection,
    handleBookmarkClick,
    notificationApi,
    notificationContextHolder,
  };
}
